import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import BrandMark from '../components/BrandMark';

const NAV = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { campaigns: true, label: 'Campaigns' },
  { id: 'contact', label: 'Contact' },
];

export default function Navbar() {
  const [activeId, setActiveId] = useState('home');
  const loc = useLocation();
  const nav = useNavigate();
  const onHome = loc.pathname === '/';

  // Scroll spy on home
  useEffect(() => {
    if (!onHome) return;
    const sections = ['home', 'about', 'services', 'batch-drop', 'contact']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id;
          setActiveId(id === 'batch-drop' ? 'services' : id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [onHome]);

  const goToSection = (id) => {
    if (!onHome) {
      nav(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scroll to hash on arrival
  useEffect(() => {
    if (onHome && loc.hash) {
      const id = loc.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [onHome, loc.hash]);

  const linkClasses = (active) =>
    `text-xs sm:text-sm tracking-wide underline-anim transition-colors whitespace-nowrap ${
      active ? 'text-[#f5f1e8] font-semibold' : 'text-[#e3d9c6] hover:text-[#f5f1e8]'
    }`;

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-[#1a3a2e] border-b border-[#f5f1e8]/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 md:py-0 md:h-20 flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-6">
        {/* Logo */}
        <div className="flex items-center justify-center md:justify-start">
          <button
            onClick={() => goToSection('home')}
            data-testid="nav-logo"
            aria-label="Co Crate Studio by Rhea Gupta — home"
          >
            <BrandMark tone="light" size="md" />
          </button>
        </div>

        {/* Visible menu — always shown */}
        <nav
          data-testid="nav-menu"
          className="flex items-center justify-center md:justify-end gap-4 sm:gap-6 md:gap-9 flex-wrap"
        >
          {NAV.map((n) => {
            if (n.campaigns) {
              return (
                <NavLink
                  key="campaigns"
                  to="/campaigns"
                  data-testid="nav-link-campaigns"
                  className={({ isActive }) => linkClasses(isActive)}
                >
                  {n.label}
                </NavLink>
              );
            }
            const active = onHome && activeId === n.id;
            return (
              <button
                key={n.id}
                onClick={() => goToSection(n.id)}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={linkClasses(active)}
              >
                {n.label}
              </button>
            );
          })}
          <button
            onClick={() => goToSection('contact')}
            data-testid="nav-cta-brew"
            className="hidden sm:inline-flex items-center px-4 md:px-5 py-2 md:py-2.5 bg-[#f5f1e8] text-[#1a3a2e] text-xs md:text-sm tracking-wide hover:bg-[#e3d9c6] transition-colors whitespace-nowrap"
          >
            Let's get brewing
          </button>
        </nav>
      </div>
    </header>
  );
}
